import { ADS_BASE_URL } from "@/shared/ui/ad/safeAdUrl";

export type AdCategory = "safe" | "r18";

/** /ads/serve が返す広告1件 */
export interface Ad {
  id: string;
  kind: "regular" | "house" | "placeholder";
  category: AdCategory;
  title: string;
  body: string;
  image_url: string;
  image_width: number | null;
  image_height: number | null;
  image_alt: string | null;
  /** 通常広告は URL 文字列。null はリンク無し広告（ブランド露出のみ）。 */
  click_url: string | null;
}

export interface BmgBannerProps {
  /** 広告枠識別子。省略時は "default"。 */
  slot?: string;
  /** 広告の配信カテゴリ。省略時は安全広告。 */
  category?: AdCategory;
  /** 広告なし確定後も枠を維持する。 */
  reserveEmptySpace?: boolean;
  /** 取得前から確保する枠の寸法。省略時は468×60。 */
  slotDimensions?: { width: number; height: number };
  /** 初期表示のLCP候補として画像取得を優先する。 */
  priority?: boolean;
  /** R18広告をぼかし、操作で開示する。 */
  concealR18?: boolean;
}

/**
 * slot ごとの広告取得結果をページロード単位でキャッシュする。
 *
 * 同接数表示（`OnlineUsersIndicatorElement`）と同じ調子で、モジュールレベルの
 * 共有 Promise に取得結果を保持する。SPA なのでカタログ⇄スレッドの画面遷移では
 * モジュールが再評価されず、同一 category/slot への `/ads/serve` は基本1回だけ走る。
 * 大量のページ表示で広告サーバ（Workers）へ毎回直撃するのを防ぐ。
 *
 * ただし SPA（特にスマホ）はフル再読み込みがほぼ発生しないため、それだけだと
 * セッション中ずっと同じ広告が固定表示されてしまう。そこで {@link AD_CACHE_TTL_MS}
 * の有効期限を設け、期限切れ後に**バナーが次にマウントされた時だけ**取り直す。
 * 画面遷移（= マウント）が「再読み込みの代わり」になる。タイマーや
 * visibilitychange 監視を使わないので、アイドル時の負荷はゼロ。
 */
const AD_CACHE_TTL_MS: number = 10 * 1000;
export const AD_DIMENSION_MAX_PX = 4096;

interface AdCacheEntry {
  promise: Promise<Ad | null>;
  /** 取得を開始した時刻（epoch ms）。TTL 判定に使う。 */
  fetchedAt: number;
}

const adCache: Map<string, AdCacheEntry> = new Map();

function parseAd(value: unknown, category: AdCategory): Ad | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const kind = record.kind;
  const imageWidth = record.image_width;
  const imageHeight = record.image_height;
  const imageAlt = record.image_alt;
  if (
    typeof record.id !== "string" ||
    (kind !== "regular" && kind !== "house" && kind !== "placeholder") ||
    record.category !== category ||
    typeof record.title !== "string" ||
    typeof record.body !== "string" ||
    typeof record.image_url !== "string" ||
    (imageWidth !== null &&
      (typeof imageWidth !== "number" ||
        !Number.isInteger(imageWidth) ||
        imageWidth <= 0 ||
        imageWidth > AD_DIMENSION_MAX_PX)) ||
    (imageHeight !== null &&
      (typeof imageHeight !== "number" ||
        !Number.isInteger(imageHeight) ||
        imageHeight <= 0 ||
        imageHeight > AD_DIMENSION_MAX_PX)) ||
    (imageAlt !== null && typeof imageAlt !== "string") ||
    (record.click_url !== null && typeof record.click_url !== "string")
  ) {
    return null;
  }
  return {
    id: record.id,
    kind,
    category,
    title: record.title,
    body: record.body,
    image_url: record.image_url,
    image_width: imageWidth,
    image_height: imageHeight,
    image_alt: imageAlt,
    click_url: record.click_url,
  };
}

function parseAdResponse(value: unknown, category: AdCategory): Ad | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.category !== category || !Array.isArray(record.ads)) return null;

  const ads = record.ads.map((ad) => parseAd(ad, category));
  if (ads.some((ad) => ad === null)) return null;
  return ads[0] ?? null;
}

export function fetchAd(
  slot: string,
  category: AdCategory,
): Promise<Ad | null> {
  const now = performance.now();
  const cacheKey = `${category}\u0000${slot}`;
  const cached = adCache.get(cacheKey);
  // TTL 内のキャッシュはそのまま再利用。期限切れ/未取得なら取り直す。
  if (cached && now - cached.fetchedAt < AD_CACHE_TTL_MS) {
    return cached.promise;
  }

  const promise = (async (): Promise<Ad | null> => {
    try {
      const params = new URLSearchParams({ slot, category, n: "1" });
      const res = await fetch(`${ADS_BASE_URL}/ads/serve?${params.toString()}`);
      // 204 は 2xx なので res.ok では弾けない。明示的に除外する。
      if (res.status === 204 || !res.ok) return null;
      return parseAdResponse(await res.json(), category);
    } catch {
      // ネットワークエラー / 障害時は広告なし扱い
      return null;
    }
  })();

  adCache.set(cacheKey, { promise, fetchedAt: now });
  return promise;
}
